import { assign, createMachine } from 'xstate';
import { OmitStrict } from '@tink/core/src/types';
import { captureMessage } from '@tink/core/src/sentry';
import { credentialsService, CredentialsService } from '../../services/credentialsService';
import { ApplicationError } from '../../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { Credentials, CredentialsEntity } from '../../../models/credentials';
import { CREDENTIALS_STATE_INVALID } from '../../../utilities/error';
import { ThirdpartyRedirector } from '../../../utilities/thirdparty-redirector/thirdparty-redirector';
import {
  shouldAutoRedirectToThirdParty,
  ShouldAutoRedirectToThirdPartyArguments,
} from '../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer';
import { POLL_INTERVAL } from '../utils/utils';
import { Typegen0 } from './TPRedirectMachine.typegen';

export type TPRedirectMachineContext = {
  credentials: CredentialsEntity;
  redirector?: ThirdpartyRedirector;
  error?: ApplicationError;
};

export type TPRedirectMachineServices = {
  getCredentials: { data: CredentialsEntity };
};

export type TPRedirectMachineEvents =
  | { type: 'USER_CLICK_REDIRECT_EVENT' }
  | { type: 'USER_CLICK_CLOSE_EVENT' };

export type TPRedirectMachineTestOverrides = {
  initialState?: Exclude<Typegen0['matchesStates'], object>;
  pollInterval?: number;
  service?: CredentialsService;
};

export type TPRedirectMachineProps = {
  initialContext: TPRedirectMachineContext;
} & OmitStrict<ShouldAutoRedirectToThirdPartyArguments, 'thirdPartyRedirector'>;

const CREDENTIALS_INVALID_STATE = 'TPRedirect Machine credentials ivalid state';

export const createTPRedirectMachine = ({
  initialContext,
  isDesktop,
  configuration,
  initialState = 'CHECK_CREDENTIALS',
  service = credentialsService,
  pollInterval = POLL_INTERVAL,
}: TPRedirectMachineProps & TPRedirectMachineTestOverrides) =>
  createMachine(
    {
      id: 'TPRedirectMachine',
      initial: initialState,
      tsTypes: {} as import('./TPRedirectMachine.typegen').Typegen0,
      schema: {
        events: {} as TPRedirectMachineEvents,
        services: {} as TPRedirectMachineServices,
        context: {} as TPRedirectMachineContext,
      },
      context: initialContext,
      predictableActionArguments: true,
      preserveActionOrder: true,
      states: {
        CHECK_CREDENTIALS: {
          always: [
            {
              target: 'APPLICATION_ERROR',
              cond: 'check_if_credentials_in_invalid_state',
              actions: [
                'capture_credentials_in_invalid_state_error',
                'throw_credentials_in_invalid_state_error',
              ],
            },
            {
              target: 'PROCESS_REDIRECTION',
              actions: 'set_redirect',
            },
          ],
        },
        APPLICATION_ERROR: {
          type: 'final',
        },
        PROCESS_REDIRECTION: {
          always: [
            {
              target: 'REDIRECT_PENDING_VIEW',
              cond: 'check_if_auto_redirect',
            },
            {
              target: 'OPEN_REDIRECT_VIEW',
            },
          ],
        },
        REDIRECT_PENDING_VIEW: {
          entry: 'open_redirect',
          initial: 'GET_CREDENTIALS',
          states: {
            GET_CREDENTIALS: {
              invoke: {
                src: 'getCredentials',
                onDone: [
                  {
                    target: 'AWAIT',
                    actions: 'set_credentials_in_context',
                  },
                ],
                onError: [
                  {
                    target: '#TPRedirectMachine.APPLICATION_ERROR',
                    actions: 'set_error_in_context',
                  },
                ],
              },
            },
            AWAIT: {
              after: {
                POLL_INTERVAL: [
                  {
                    target: '#TPRedirectMachine.REDIRECT_PENDING_VIEW.GET_CREDENTIALS',
                  },
                ],
              },
            },
          },
          always: {
            target: 'FLOW_FINISHED',
            cond: 'check_if_credentials_status_changed',
          },
        },
        OPEN_REDIRECT_VIEW: {
          on: {
            USER_CLICK_REDIRECT_EVENT: {
              target: 'REDIRECT_PENDING_VIEW',
            },
          },
        },
        FLOW_FINISHED: {
          type: 'final',
        },
      },
    },
    {
      guards: {
        check_if_credentials_in_invalid_state: ({ credentials }) =>
          !Credentials.isAwaitingThirdPartyAppAuthentication(credentials) ||
          !credentials.supplementalInformation,
        check_if_auto_redirect: ({ redirector }) =>
          !!shouldAutoRedirectToThirdParty({
            isDesktop,
            configuration,
            thirdPartyRedirector: redirector,
          }),
        check_if_credentials_status_changed: ({ credentials }) =>
          !Credentials.isAwaitingThirdPartyAppAuthentication(credentials),
      },
      actions: {
        set_credentials_in_context: assign({
          credentials: (_, event) => event.data,
        }),
        set_error_in_context: assign({
          error: (_, event) => event.data as ApplicationError,
        }),
        throw_credentials_in_invalid_state_error: assign({
          error: (_) => new ApplicationError(CREDENTIALS_STATE_INVALID),
        }),
        set_redirect: assign({
          redirector: ({ credentials }) =>
            new ThirdpartyRedirector({
              configuration,
              supplementalInformation: credentials.supplementalInformation!,
            }),
        }),
        open_redirect: ({ redirector }) => redirector?.navigate(),
        capture_credentials_in_invalid_state_error: ({ credentials }) => {
          captureMessage(CREDENTIALS_INVALID_STATE, {
            custom: {
              credentialsState: credentials.status,
            },
          });
        },
      },
      services: {
        getCredentials: ({ credentials }) => service.getCredentials(credentials.id),
      },
      delays: {
        POLL_INTERVAL: pollInterval,
      },
    }
  );
